import HighchartsReact, {
  HighchartsReactProps,
  HighchartsReactRefObject,
} from "highcharts-react-official"
import Highcharts, { SeriesHeatmapOptions } from "highcharts"
import HighchartsHeatmap from "highcharts/modules/heatmap"
import CustomEvents from "highcharts-custom-events"
import { useEffect, useMemo, useRef } from "react"

HighchartsHeatmap(Highcharts)
CustomEvents(Highcharts)

export interface TmScoreMapProps {
  /**
   * The ID used to identify this component in Dash callbacks.
   */
  id?: string

  /**
   * List with the name of the vendors
   */
  vendors: string[]
  /**
   * List with the name of the elements
   */
  elements: string[]
  /**
   * List with the tooltip for each element
   */
  element_tooltips?: string[]
  /**
   * Dict with key as string with the vendor name, value as list with scores (one for each element)
   *  scores -> 1 below benchmark, 2 in benchmark, 3 above benchmark
   */
  vendor_scores: {
    [vendor: string]: (0 | 1 | 2 | 3)[]
  }
  /**
   * Whether to generate SVG or not
   */
  generate_svg?: boolean
  /**
   * SVG string
   */
  svg?: string
  /**
   * Maximum height of the chart
   */
  max_height?: number
  /**
   * Dash-assigned callback that should be called to report property changes
   * to Dash, to make them available for callbacks.
   */
  setProps: (props: Partial<TmScoreMapProps>) => void
}

const colors = {
  belowBenchmark: "#73C9F9",
  benchmark: "#4B99F7",
  aboveBenchmark: "#184389",
}

const superscriptEntities: { [number: string]: string } = {
  "0": "&#8304;",
  "1": "&#185;",
  "2": "&#178;",
  "3": "&#179;",
  "4": "&#8308;",
  "5": "&#8309;",
  "6": "&#8310;",
  "7": "&#8311;",
  "8": "&#8312;",
  "9": "&#8313;",
}

const getOptions = (
  vendors: string[],
  elements: string[],
  elementTooltips: string[] | undefined,
  data: SeriesHeatmapOptions["data"],
  maxHeight: number = Infinity,
  generateSvg: boolean = false
): HighchartsReactProps["options"] => {
  const elementsWithLegend = generateSvg
    ? elements.map((element, index) => {
        const position = (elements.length - index).toString()
        let legend = ""
        for (let i = 0; i < position.length; i++) {
          legend += superscriptEntities[position[i]]
        }
        return `${element}<div>${legend}</div>`
      })
    : elements

  const createTooltip = (category: any, e: MouseEvent) => {
    if (elementTooltips && elementTooltips[category.pos]) {
      category.chart.myLabel = category.chart.renderer
        .label(
          elementTooltips[category.pos],
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          e.layerX + 1,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          e.layerY - (elementTooltips[category.pos].length > 150 ? 40 : 20),
          "rectangle"
        )
        .css({ color: "#FFFFFF", width: 550 })
        .attr({
          fill: "rgba(0, 0, 0, 0.75)",
          padding: 8,
          r: 4,
        })
        .add()
        .toFront()
    }
  }
  const destroyTooltip = (category: any) => {
    category.chart.myLabel?.destroy()
  }

  const height =
    10 + Math.max(...vendors.map((c) => c.length)) * 5 + elements.length * 30 + vendors.length * 10

  return {
    chart: {
      type: "heatmap",
      plotBorderWidth: 1,
      height: Math.min(height, maxHeight),
    },
    credits: {
      enabled: false,
    },
    title: {
      text: "",
    },
    xAxis: {
      categories: vendors,
      labels: {
        style: {
          color: "#000000",
          fontSize: "14px",
          textOverflow: "none",
          whiteSpace: "nowrap",
        },
        autoRotation: [45],
        autoRotationLimit: 150,
      },
      opposite: true,
    },
    yAxis: {
      categories: elementsWithLegend,
      labels: {
        style: {
          color: "#000000",
          fontSize: "12px",
          textOverflow: "none",
          lineHeight: "14px",
        },
        /* eslint-disable */
        // @ts-ignore
        events:
          !generateSvg && elementTooltips
            ? {
                mouseover: function (e: MouseEvent) {
                  createTooltip(this, e)
                },
                mousemove: function (e: MouseEvent) {
                  destroyTooltip(this)
                  createTooltip(this, e)
                },
                mouseout: function () {
                  destroyTooltip(this)
                },
              }
            : {},
        /* eslint-enable */
      },
      title: {
        text: "",
      },
    },
    plotOptions: {
      series: {
        states: {
          hover: {
            enabled: false,
          },
        },
      },
    },
    accessibility: {
      enabled: false,
    },
    legend: {
      enabled: false,
    },
    tooltip: {
      enabled: false,
    },
    series: [
      {
        name: "",
        borderWidth: 1,
        data: data,
        borderColor: "#000000",
        pointWidth: 1,
        type: "heatmap",
      } as SeriesHeatmapOptions,
    ],
    exporting: {
      enabled: false,
    },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
          },
        },
      ],
    },
  }
}

const TmScoreMap = ({
  vendors,
  elements,
  element_tooltips = undefined,
  vendor_scores,
  generate_svg = false,
  max_height = Infinity,
  setProps,
}: TmScoreMapProps) => {
  if (generate_svg == null) generate_svg = false
  if (element_tooltips == null) element_tooltips = undefined
  if (max_height == null) max_height = Infinity

  const chartRef = useRef<HighchartsReactRefObject>(null)

  const options = useMemo(() => {
    const data: SeriesHeatmapOptions["data"] = []
    vendors.forEach((vendor, vendorIndex) => {
      vendor_scores[vendor].forEach((benchmark, benchmarkIndex) => {
        let benchmarkColor = ""
        switch (benchmark) {
          case 0:
            benchmarkColor = colors["belowBenchmark"]
            break
          case 1:
            benchmarkColor = colors["benchmark"]
            break
          case 2:
          case 3:
            benchmarkColor = colors["aboveBenchmark"]
            break
        }
        data.push({ x: vendorIndex, y: benchmarkIndex, value: benchmark, color: benchmarkColor })
      })
    })
    return getOptions(vendors, elements, element_tooltips, data, max_height, generate_svg)
  }, [vendor_scores, vendors, elements, element_tooltips, max_height, generate_svg])

  useEffect(() => {
    if (generate_svg) {
      const chart = chartRef.current?.chart
      if (chart != undefined) {
        setProps({ svg: chart.getSVG() })
      }
    }
  }, [generate_svg, options])

  return (
    <div>
      <div>
        <ol style={{ display: "flex", flexDirection: "row", justifyContent: "space-around" }}>
          {Object.entries(colors).map(([classification, color]) => (
            <li key={classification} style={{ listStyle: "none" }}>
              <span
                style={{
                  backgroundColor: color,
                  height: "14px",
                  width: "14px",
                  borderRadius: "2px",
                  display: "inline-block",
                  marginRight: "8px",
                }}
              />
              {classification == "aboveBenchmark"
                ? "Above Benchmark"
                : classification == "benchmark"
                ? "Benchmark"
                : "Below Benchmark"}
            </li>
          ))}
        </ol>
      </div>
      <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
    </div>
  )
}

export default TmScoreMap
